import dayjs from 'dayjs';
import { formatAmount } from '../../../lib/format/amount';
import { logger } from '../../../lib/logger';

type NumberUnit = 'thousand' | 'yen' | undefined;

type TranslationFunction = (key: string, options?: Record<string, unknown>) => string;

/**
 * 数値を単位に応じた金額に変換
 */
const convertAmountByUnit = (value: number, unit: NumberUnit): number => {
  switch (unit) {
    case 'thousand':
      // 千円表示は切り捨てで安全に整形する
      return Math.floor(value / 1000);
    case 'yen':
      return value;
    default: {
      const check: never | undefined = unit;
      if (unit) {
        logger.error('使用されてない単位定義があります', check);
      }
      return value;
    }
  }
};

/**
 * 金額を単位付きの文字列にフォーマット
 */
const formatAmountWithUnit = (amount: string, unit: NumberUnit, t: TranslationFunction): string => {
  switch (unit) {
    case 'yen':
      return t('common.unit.amount-yen', { amount });
    case 'thousand':
      return t('common.unit.amount-thousand-yen', { amount });
    default: {
      const check: never | undefined = unit;
      if (unit) {
        logger.error('使用されてない単位定義があります', check);
      }
      return amount;
    }
  }
};

/**
 * 数値をフォーマット
 *
 * @param value - フォーマットする数値
 * @param unit - 単位（'thousand' | 'yen'）
 * @param t - i18next の翻訳関数
 * @returns フォーマットされた文字列
 */
export const formatNumber = (value: number, unit: NumberUnit, t: TranslationFunction): string => {
  const amountByUnit = convertAmountByUnit(value, unit);
  const amount = formatAmount(amountByUnit);
  return formatAmountWithUnit(amount, unit, t);
};

/**
 * 日付をフォーマット
 *
 * @param value - フォーマットする日付文字列
 * @param format - 日付フォーマット（デフォルト: 'YYYY/MM/DD HH:mm'）
 * @returns フォーマットされた文字列、無効な日付の場合はnull
 */
export const formatDate = (value: string, format: string): string | null => {
  const date = dayjs(value);
  if (date.isValid()) {
    return date.format(format);
  }
  return null;
};
